import { memo, useState } from "react";

import { InputPrefixSuffix } from "../formControl";
import { InputV2 } from "../input";

import classes from "./styles.module.scss";

import type {
  IInputFormControlProps,
  IPhoneNumber,
  IValidation,
} from "@components/formControls";
import { useReadOnly } from "@/hooks";
import { formatNumber, isPhoneValid } from "@/utils";
import { Typography } from "@/components/atoms/typography";

const DEFAULT_CODE = "+98";

function getPhoneValidation(
  isValid: boolean | undefined,
): IValidation | undefined {
  if (isValid === undefined) {
    return undefined;
  }

  return isValid ? "success" : "warning";
}

function CodeComp(code: string) {
  return (
    <div dir={"ltr"} className={classes.codeContainer}>
      <span className={`fi fi-ir`} />
      <Typography color={"primary-main"}>{code}</Typography>
    </div>
  );
}

export const Phone = memo(function Phone(
  props: IInputFormControlProps<IPhoneNumber>,
) {
  const {
    value = { code: DEFAULT_CODE, number: "" },
    onChange,
    onFocus,
    onBlur,
    disabled,
    placeholder,
    readOnly: propsReadOnly,
    validation: propsValidation,
    ...rest
  } = props;

  const readOnly = useReadOnly(propsReadOnly);

  const [isInputFocused, setIsInputFocused] = useState(false);

  const validation: IValidation | undefined = isInputFocused
    ? getPhoneValidation(isPhoneValid(value))
    : propsValidation;

  function inputChangeHandler(_value: string) {
    // Remove non-numeric characters
    const numericValue = _value.replaceAll(/[^\d۰-۹]/g, "");
    const trimmedNumericValue = numericValue.slice(0, 10);

    onChange?.({
      code: value.code,
      number: formatNumber({ code: value.code, number: trimmedNumericValue }),
    });
  }

  return (
    <InputV2
      dir={"ltr"}
      placeholder={"9-- --- ----"}
      suffixReact={
        <InputPrefixSuffix>{CodeComp(value.code)}</InputPrefixSuffix>
      }
      value={value.number}
      validation={validation}
      readOnly={readOnly}
      disabled={disabled}
      onChange={inputChangeHandler}
      onFocus={(event) => {
        setIsInputFocused(true);
        onFocus?.(event);
      }}
      onBlur={(event) => {
        setIsInputFocused(false);
        onBlur?.(event);
      }}
      {...rest}
    />
  );
});
